import { useEffect, useState, useCallback, useMemo } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useApi } from '../hooks/useApi'
import { formatDate, formatTimestamp, formatDuration } from '../utils/formatters'
import type { TripMode, TripStatisticsSummaryResponse, TripStatisticsListResponse } from '../types'
import './TripStatistics.css'

const MODES: { value: TripMode | 'all'; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'trip', label: 'Trip' },
  { value: 'today', label: 'Today' },
  { value: 'week', label: 'Week' },
  { value: 'month', label: 'Month' },
  { value: 'user1', label: 'User1' },
  { value: 'user2', label: 'User2' },
  { value: 'refuel', label: 'Refuel' },
  { value: 'adblue_refuel', label: 'AdBlue' },
  { value: 'since_regen', label: 'DPF Regen' },
]

const DATE_RANGES = [
  { value: 'all', label: 'All time' },
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: 'year', label: 'This year' },
] as const

type DateRange = typeof DATE_RANGES[number]['value']
type SortBy = 'date' | 'distance' | 'consumption'

function getGoogleMapsUrl(lat: number, lon: number): string {
  return `https://www.google.com/maps?q=${lat},${lon}`
}

function isInDateRange(timestamp: string, range: DateRange): boolean {
  if (range === 'all') return true
  const date = new Date(timestamp)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffDays = diffMs / 86400000

  if (range === '7d') return diffDays <= 7
  if (range === '30d') return diffDays <= 30
  if (range === 'year') return date.getFullYear() === now.getFullYear()
  return true
}

function formatKmPerLiter(value: number | undefined): string {
  if (value === undefined || value === null) return '--'
  return value.toFixed(1)
}

function formatDistance(value: number | undefined): string {
  if (value === undefined || value === null) return '--'
  return value.toFixed(1)
}

function formatLiters(value: number | undefined): string {
  if (value === undefined || value === null) return '--'
  return value.toFixed(1)
}

export function TripStatisticsPage() {
  const { user } = useAuth()
  const [selectedMode, setSelectedMode] = useState<TripMode | 'all'>('all')
  const [dateRange, setDateRange] = useState<DateRange>('all')
  const [showUnfinalized, setShowUnfinalized] = useState(true)
  const [sortBy, setSortBy] = useState<SortBy>('date')
  const [expandedId, setExpandedId] = useState<number | null>(null)
  const [page, setPage] = useState(0)
  const PAGE_SIZE = 25

  const { data: summaryData, loading: summaryLoading } = useApi<TripStatisticsSummaryResponse>('/api/v1/trip-statistics/summary', { skip: !user })

  const [listData, setListData] = useState<TripStatisticsListResponse | null>(null)
  const [listLoading, setListLoading] = useState(false)
  const [listError, setListError] = useState<string | null>(null)

  const fetchList = useCallback(async () => {
    if (!user) return
    setListLoading(true)
    setListError(null)

    try {
      const params = new URLSearchParams()
      if (selectedMode !== 'all') {
        params.set('mode', selectedMode)
      }
      if (!showUnfinalized) {
        params.set('finalized', 'true')
      }
      params.set('limit', String(PAGE_SIZE))
      params.set('offset', String(page * PAGE_SIZE))

      const response = await fetch(`/api/v1/trip-statistics?${params.toString()}`, {
        credentials: 'same-origin',
        cache: 'no-store',
      })

      if (!response.ok) {
        throw new Error('Failed to fetch trip statistics')
      }

      const data = await response.json()
      setListData(data)
    } catch (err) {
      setListError(err instanceof Error ? err.message : 'Unknown error')
    } finally {
      setListLoading(false)
    }
  }, [user, selectedMode, showUnfinalized, page])

  useEffect(() => {
    fetchList()
  }, [fetchList])

  // Reset page when filters change
  useEffect(() => {
    setPage(0)
  }, [selectedMode, dateRange, showUnfinalized, sortBy])

  const filteredItems = useMemo(() => {
    if (!listData) return []

    let items = [...listData.items]

    // Date range filter
    if (dateRange !== 'all') {
      items = items.filter(item => isInDateRange(item.start_timestamp, dateRange))
    }

    // Sorting
    items.sort((a, b) => {
      if (sortBy === 'date') {
        return new Date(b.start_timestamp).getTime() - new Date(a.start_timestamp).getTime()
      }
      if (sortBy === 'distance') {
        return (b.distance_km ?? 0) - (a.distance_km ?? 0)
      }
      if (sortBy === 'consumption') {
        return (b.avg_km_per_liter ?? 0) - (a.avg_km_per_liter ?? 0)
      }
      return 0
    })

    return items
  }, [listData, dateRange, sortBy])

  const visibleSummary = useMemo(() => {
    if (!summaryData) return null
    if (selectedMode === 'all') {
      // Aggregate across all modes
      const allSummaries = Object.values(summaryData).filter(Boolean)
      if (allSummaries.length === 0) return null
      return {
        total_count: allSummaries.reduce((sum, s) => sum + s.total_count, 0),
        total_distance_km: allSummaries.reduce((sum, s) => sum + s.total_distance_km, 0),
        total_liters_consumed: allSummaries.reduce((sum, s) => sum + s.total_liters_consumed, 0),
        avg_km_per_liter: allSummaries.reduce((sum, s) => sum + s.avg_km_per_liter * s.total_count, 0) /
          allSummaries.reduce((sum, s) => sum + s.total_count, 0) || 0,
        total_driving_time_seconds: allSummaries.reduce((sum, s) => sum + s.total_driving_time_seconds, 0),
        avg_distance_per_trip_km: allSummaries.reduce((sum, s) => sum + s.avg_distance_per_trip_km * s.total_count, 0) /
          allSummaries.reduce((sum, s) => sum + s.total_count, 0) || 0,
        best_km_per_liter: Math.max(...allSummaries.map(s => s.best_km_per_liter)),
        worst_km_per_liter: Math.min(...allSummaries.map(s => s.worst_km_per_liter)),
      }
    }
    return summaryData[selectedMode] || null
  }, [summaryData, selectedMode])

  const maxConsumption = useMemo(() => {
    if (filteredItems.length === 0) return 1
    return Math.max(...filteredItems.map(i => i.avg_km_per_liter ?? 0), 1)
  }, [filteredItems])

  const toggleExpanded = (id: number) => {
    setExpandedId(prev => prev === id ? null : id)
  }

  const loading = summaryLoading || listLoading

  return (
    <div className="page trip-statistics">
      <h1>Trip Statistics</h1>

      {/* Summary Cards */}
      {visibleSummary && (
        <div className="summary-cards">
          <div className="summary-card">
            <span className="summary-value">{visibleSummary.total_count}</span>
            <span className="summary-label">Total Trips</span>
          </div>
          <div className="summary-card">
            <span className="summary-value">{visibleSummary.total_distance_km.toFixed(0)}</span>
            <span className="summary-label">Distance (km)</span>
          </div>
          <div className="summary-card">
            <span className="summary-value">{visibleSummary.total_liters_consumed.toFixed(1)}</span>
            <span className="summary-label">Fuel (L)</span>
          </div>
          <div className="summary-card">
            <span className="summary-value">{visibleSummary.avg_km_per_liter.toFixed(1)}</span>
            <span className="summary-label">Avg km/L</span>
          </div>
        </div>
      )}

      {/* Mode Tabs */}
      <div className="mode-tabs">
        {MODES.map(mode => (
          <button
            key={mode.value}
            className={`mode-tab ${selectedMode === mode.value ? 'mode-tab--active' : ''}`}
            onClick={() => setSelectedMode(mode.value)}
          >
            {mode.label}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="filters">
        <div className="filter-group">
          <label className="filter-label">Date Range</label>
          <select
            className="filter-select"
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value as DateRange)}
          >
            {DATE_RANGES.map(range => (
              <option key={range.value} value={range.value}>{range.label}</option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label className="filter-label">Sort By</label>
          <select
            className="filter-select"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as SortBy)}
          >
            <option value="date">Date (newest)</option>
            <option value="distance">Distance</option>
            <option value="consumption">Consumption</option>
          </select>
        </div>

        <div className="filter-group filter-group--checkbox">
          <label className="filter-checkbox">
            <input
              type="checkbox"
              checked={showUnfinalized}
              onChange={(e) => setShowUnfinalized(e.target.checked)}
            />
            <span>Show in-progress</span>
          </label>
        </div>
      </div>

      {/* Bar Chart */}
      {filteredItems.length > 0 && (
        <div className="card chart-card">
          <div className="card-header">
            <h3 className="card-title">Consumption Over Time</h3>
            <span className="badge badge--muted">km/L</span>
          </div>
          <div className="chart-container">
            <div className="bar-chart trip-bar-chart">
              {filteredItems.slice(0, 30).map((item, index) => (
                <div key={index} className="bar-wrapper">
                  <div
                    className="bar-container"
                    title={`${formatKmPerLiter(item.avg_km_per_liter)} km/L - ${formatDate(item.start_timestamp)}`}
                  >
                    <div
                      className="bar bar--trip"
                      style={{ height: `${((item.avg_km_per_liter ?? 0) / maxConsumption) * 100}%` }}
                    />
                  </div>
                  <div className="bar-label">{formatDate(item.start_timestamp)}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Trip List */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">
            {selectedMode === 'all' ? 'All Trips' : MODES.find(m => m.value === selectedMode)?.label}
          </h3>
          <span className="badge badge--muted">
            {listData?.total ?? 0} total
          </span>
        </div>

        {loading && (
          <div className="loading-state">
            <p>Loading...</p>
          </div>
        )}

        {listError && (
          <div className="error-state">
            <p className="text-danger">Error: {listError}</p>
          </div>
        )}

        {!loading && !listError && filteredItems.length === 0 && (
          <div className="no-data">
            <p className="text-muted">No trip statistics available</p>
          </div>
        )}

        {!loading && !listError && filteredItems.length > 0 && (
          <div className="trip-list">
            {filteredItems.map(item => (
              <div key={item.id} className={`trip-item ${!item.is_finalized ? 'trip-item--unfinalized' : ''}`}>
                <div
                  className="trip-row"
                  onClick={() => toggleExpanded(item.id)}
                >
                  <div className="trip-cell trip-cell--mode">
                    <span className={`mode-badge mode-badge--${item.trip_mode}`}>
                      {MODES.find(m => m.value === item.trip_mode)?.label ?? item.trip_mode}
                    </span>
                    {item.trip_name && (
                      <span className="trip-name">{item.trip_name}</span>
                    )}
                    {!item.is_finalized && (
                      <span className="badge badge--warning">In Progress</span>
                    )}
                  </div>
                  <div className="trip-cell trip-cell--date">
                    <div className="trip-date">{formatDate(item.start_timestamp)}</div>
                    <div className="trip-time">{formatTimestamp(item.start_timestamp)}</div>
                  </div>
                  <div className="trip-cell trip-cell--stats">
                    <div className="trip-stat">
                      <span className="trip-stat-value">{formatDistance(item.distance_km)}</span>
                      <span className="trip-stat-label">km</span>
                    </div>
                    <div className="trip-stat">
                      <span className="trip-stat-value">{formatLiters(item.liters_consumed)}</span>
                      <span className="trip-stat-label">L</span>
                    </div>
                    <div className="trip-stat">
                      <span className="trip-stat-value">{formatKmPerLiter(item.avg_km_per_liter)}</span>
                      <span className="trip-stat-label">km/L</span>
                    </div>
                  </div>
                  <div className="trip-cell trip-cell--expand">
                    <span className={`expand-icon ${expandedId === item.id ? 'expand-icon--open' : ''}`}>
                      ▼
                    </span>
                  </div>
                </div>

                {/* Expanded Detail */}
                {expandedId === item.id && (
                  <div className="trip-detail">
                    <div className="detail-grid">
                      <div className="detail-section">
                        <h4 className="detail-section-title">Time</h4>
                        <div className="detail-row">
                          <span className="detail-label">Start:</span>
                          <span className="detail-value">{formatTimestamp(item.start_timestamp)}</span>
                        </div>
                        <div className="detail-row">
                          <span className="detail-label">End:</span>
                          <span className="detail-value">{formatTimestamp(item.end_timestamp)}</span>
                        </div>
                        <div className="detail-row">
                          <span className="detail-label">Duration:</span>
                          <span className="detail-value">
                            {item.driving_time_seconds ? formatDuration(item.driving_time_seconds) : '--'}
                          </span>
                        </div>
                      </div>

                      <div className="detail-section">
                        <h4 className="detail-section-title">Performance</h4>
                        <div className="detail-row">
                          <span className="detail-label">Avg Speed:</span>
                          <span className="detail-value">{item.avg_speed_kmh?.toFixed(1) ?? '--'} km/h</span>
                        </div>
                        <div className="detail-row">
                          <span className="detail-label">Idle Fuel:</span>
                          <span className="detail-value">{formatLiters(item.idle_liters)} L ({item.idle_percent?.toFixed(1) ?? '--'}%)</span>
                        </div>
                        <div className="detail-row">
                          <span className="detail-label">Free Distance:</span>
                          <span className="detail-value">{formatDistance(item.free_distance_km)} km ({item.free_percent?.toFixed(1) ?? '--'}%)</span>
                        </div>
                      </div>

                      <div className="detail-section">
                        <h4 className="detail-section-title">Starts & DPF</h4>
                        <div className="detail-row">
                          <span className="detail-label">Cold:</span>
                          <span className="detail-value">{item.cold_starts ?? '--'}</span>
                        </div>
                        <div className="detail-row">
                          <span className="detail-label">Warm:</span>
                          <span className="detail-value">{item.warm_starts ?? '--'}</span>
                        </div>
                        <div className="detail-row">
                          <span className="detail-label">DPF Regens:</span>
                          <span className="detail-value">{item.dpf_regens ?? '--'}</span>
                        </div>
                      </div>

                      <div className="detail-section">
                        <h4 className="detail-section-title">Location</h4>
                        {item.start_location ? (
                          <div className="detail-row">
                            <span className="detail-label">Start:</span>
                            <a
                              href={getGoogleMapsUrl(item.start_location.lat, item.start_location.lon)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="detail-link"
                            >
                              {item.start_location.lat.toFixed(4)}, {item.start_location.lon.toFixed(4)}
                            </a>
                          </div>
                        ) : (
                          <div className="detail-row">
                            <span className="detail-label">Start:</span>
                            <span className="detail-value text-muted">N/A</span>
                          </div>
                        )}
                        {item.end_location ? (
                          <div className="detail-row">
                            <span className="detail-label">End:</span>
                            <a
                              href={getGoogleMapsUrl(item.end_location.lat, item.end_location.lon)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="detail-link"
                            >
                              {item.end_location.lat.toFixed(4)}, {item.end_location.lon.toFixed(4)}
                            </a>
                          </div>
                        ) : (
                          <div className="detail-row">
                            <span className="detail-label">End:</span>
                            <span className="detail-value text-muted">N/A</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {listData && listData.total > PAGE_SIZE && (
          <div className="pagination">
            <button
              className="pagination-btn"
              disabled={page === 0}
              onClick={() => setPage(p => p - 1)}
            >
              Previous
            </button>
            <span className="pagination-info">
              Page {page + 1} of {Math.ceil(listData.total / PAGE_SIZE)}
            </span>
            <button
              className="pagination-btn"
              disabled={(page + 1) * PAGE_SIZE >= listData.total}
              onClick={() => setPage(p => p + 1)}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
